import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useTenant } from '../../contexts/TenantContext'
import { useDocumentTitle } from '../../hooks/useDocumentTitle'
import { supabase } from '../../lib/supabase'
import { MainLayout } from '../../components/layouts/MainLayout'
import { Loading } from '../../components/common/Loading'
import { ErrorMessage } from '../../components/common/ErrorMessage'
import { BookOpen, Award, Clock, AlertCircle } from 'lucide-react'
import { PendingApply, Course } from '../../lib/types'

interface EnrolledCourse {
  course_id: string
  title: string
  description: string | null
  tenant_name: string
  valid_until: string | null
  lessons_completed: number
  total_lessons: number
}

const PENDING_APPLY_KEY = 'pending_apply'

export function MyPage() {
  useDocumentTitle('マイページ')
  const navigate = useNavigate()
  const { user, profile } = useAuth()
  const { currentTenant, memberships, isPlatformAdmin } = useTenant()
  const [courses, setCourses] = useState<EnrolledCourse[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pendingApply, setPendingApply] = useState<PendingApply | null>(null)
  const [pendingCourse, setPendingCourse] = useState<Course | null>(null)

  useEffect(() => {
    // プラットフォーム管理者チェック
    if (isPlatformAdmin) {
      navigate('/admin/dashboard', { replace: true })
      return
    }

    // テナント管理者チェック
    const isTenantAdmin = memberships.some((m) => m.role_in_tenant === 'tenant_admin')
    if (isTenantAdmin && memberships.length > 0) {
      navigate('/tenant/dashboard', { replace: true })
      return
    }

    // 通常ユーザーまたはmembershipsがまだ読み込まれていない場合のみデータ取得
    if (!isPlatformAdmin && !isTenantAdmin) {
      fetchEnrolledCourses()
      checkPendingApply()
    }
  }, [user, currentTenant])

  const fetchEnrolledCourses = async () => {
    if (!user) return

    try {
      setLoading(true)
      setError(null)

      // entitlements から受講中の講座を取得
      const { data: entitlements, error: entitlementsError } = await supabase
        .from('entitlements')
        .select(
          `
          entitlement_id,
          course_id,
          valid_until,
          courses (
            course_id,
            title,
            description,
            tenant_id,
            tenants (
              name
            )
          )
        `
        )
        .eq('user_id', user.id)
        .eq('status', 'active')

      if (entitlementsError) throw entitlementsError

      // 各講座の進捗を取得
      const coursesData: EnrolledCourse[] = []
      for (const ent of entitlements || []) {
        const course = ent.courses as any
        const tenantName = course.tenants?.name || ''

        // レッスン総数
        const { count: totalLessons } = await supabase
          .from('course_lessons')
          .select('*', { count: 'exact', head: true })
          .eq('course_id', course.course_id)

        // 完了レッスン数
        const { count: completedLessons } = await supabase
          .from('lesson_progress')
          .select('*', { count: 'exact', head: true })
          .eq('course_id', course.course_id)
          .eq('user_id', user.id)
          .eq('status', 'completed')

        coursesData.push({
          course_id: course.course_id,
          title: course.title,
          description: course.description,
          tenant_name: tenantName,
          valid_until: ent.valid_until,
          lessons_completed: completedLessons || 0,
          total_lessons: totalLessons || 0,
        })
      }

      setCourses(coursesData)
    } catch (err: any) {
      console.error('Error fetching courses:', err)
      setError(err.message || '講座の取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  const checkPendingApply = async () => {
    try {
      const storedData = sessionStorage.getItem(PENDING_APPLY_KEY)
      if (!storedData) return

      const pending: PendingApply = JSON.parse(storedData)

      // 有効期限チェック
      if (new Date(pending.expires_at) < new Date()) {
        sessionStorage.removeItem(PENDING_APPLY_KEY)
        return
      }

      // 講座情報を取得
      const { data: courseData, error: courseError } = await supabase
        .from('courses')
        .select('*')
        .eq('course_public_key', pending.course)
        .eq('status', 'published')
        .single()

      if (courseError || !courseData) {
        sessionStorage.removeItem(PENDING_APPLY_KEY)
        return
      }

      setPendingApply(pending)
      setPendingCourse(courseData)
    } catch (err) {
      console.error('Error checking pending apply:', err)
      sessionStorage.removeItem(PENDING_APPLY_KEY)
    }
  }

  const handleProceedToEnrollment = () => {
    if (!pendingApply) return

    const params = new URLSearchParams({
      course: pendingApply.course,
    })
    if (pendingApply.run) {
      params.set('run', pendingApply.run)
    }
    navigate(`/enrollment/confirm?${params.toString()}`)
  }

  const handleDismissPendingApply = () => {
    sessionStorage.removeItem(PENDING_APPLY_KEY)
    setPendingApply(null)
    setPendingCourse(null)
  }

  if (loading) {
    return (
      <MainLayout>
        <Loading message="講座を読み込み中..." />
      </MainLayout>
    )
  }

  // 統計データを計算
  const totalCourses = courses.length
  const completedLessons = courses.reduce((acc, c) => acc + c.lessons_completed, 0)
  const totalLessons = courses.reduce((acc, c) => acc + c.total_lessons, 0)
  const avgProgress =
    totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0

  return (
    <MainLayout>
      <div className="min-h-screen bg-[#f9fafb]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-10">
          {/* Welcome Section */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-base font-bold text-gray-900">
                こんにちは、{profile?.full_name || user?.email}さん
              </h1>
              <p className="text-gray-500 mt-1">受講中の講座を確認できます</p>
            </div>
          </div>

          {error && (
            <div className="mb-6">
              <ErrorMessage message={error} onRetry={fetchEnrolledCourses} />
            </div>
          )}

          {pendingApply && pendingCourse && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <AlertCircle className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">
                    講座への申込を続けますか？
                  </h3>
                  <p className="text-sm text-blue-800 mb-4">
                    「{pendingCourse.title}」への申込手続きが保留されています。
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={handleProceedToEnrollment}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium shadow-sm"
                    >
                      申込を続ける
                    </button>
                    <button
                      onClick={handleDismissPendingApply}
                      className="px-4 py-2 bg-white text-blue-700 border border-blue-300 rounded-lg hover:bg-blue-50 transition text-sm font-medium"
                    >
                      後で申込む
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Stats Overview */}
          {courses.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 flex items-center gap-4">
                <div className="p-3 bg-blue-50 text-[#006cc6] rounded-lg">
                  <BookOpen size={24} />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">受講中の講座</p>
                  <p className="text-2xl font-bold text-gray-900">{totalCourses}</p>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 flex items-center gap-4">
                <div className="p-3 bg-green-50 text-green-600 rounded-lg">
                  <Award size={24} />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">完了レッスン</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {completedLessons}/{totalLessons}
                  </p>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 flex items-center gap-4">
                <div className="p-3 bg-purple-50 text-purple-600 rounded-lg">
                  <Clock size={24} />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">平均進捗率</p>
                  <p className="text-2xl font-bold text-gray-900">{avgProgress}%</p>
                </div>
              </div>
            </div>
          )}

          {/* Courses Section */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">受講中の講座</h2>
            {courses.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center">
                <BookOpen className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-4 text-lg font-medium text-gray-900">
                  受講中の講座がありません
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  講座を購入すると、ここに表示されます
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {courses.map((course) => {
                  const progressPercentage =
                    course.total_lessons > 0
                      ? (course.lessons_completed / course.total_lessons) * 100
                      : 0
                  return (
                    <div
                      key={course.course_id}
                      className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden"
                    >
                      <div className="p-6">
                        <div className="flex items-start justify-between mb-3">
                          <h3 className="text-lg font-semibold text-gray-900 flex-1 line-clamp-2">
                            {course.title}
                          </h3>
                        </div>

                        <p className="text-sm text-gray-600 mb-4 line-clamp-2 min-h-[2.5rem]">
                          {course.description || '講座の説明はありません'}
                        </p>

                        <div className="space-y-3">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <BookOpen className="h-4 w-4 flex-shrink-0" />
                            <span>
                              進捗: {course.lessons_completed}/{course.total_lessons} レッスン
                            </span>
                          </div>

                          {course.valid_until && (
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Clock className="h-4 w-4 flex-shrink-0" />
                              <span>
                                有効期限:{' '}
                                {new Date(course.valid_until).toLocaleDateString('ja-JP')}
                              </span>
                            </div>
                          )}

                          <div className="pt-2">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-medium text-gray-500">
                                学習進捗
                              </span>
                              <span className="text-xs font-bold text-gray-900">
                                {Math.round(progressPercentage)}%
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-[#006cc6] h-2 rounded-full transition-all duration-500"
                                style={{
                                  width: `${progressPercentage}%`,
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="px-6 pb-6">
                        <a
                          href={`/courses/${course.course_id}`}
                          className="block w-full text-center px-4 py-2.5 bg-[#006cc6] hover:bg-[#06579e] text-white rounded-lg transition-colors font-medium text-sm shadow-sm shadow-blue-500/20"
                        >
                          講座を開く
                        </a>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </section>
        </div>
      </div>
    </MainLayout>
  )
}
